import { useState, useEffect, useRef, KeyboardEvent } from 'react'
import { zeusChatApi, type ZeusChatMessage, type ZeusChatResponseRecord } from '@/lib/api'

// Zeus runs in the background and can take minutes. Poll its response record
// until it resolves. Generous ceiling so a slow/rate-limited Anthropic call
// still lands rather than timing out the UI prematurely.
const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 5 * 60 * 1000

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function pollForResponse(conversationId: string): Promise<ZeusChatResponseRecord> {
  const deadline = Date.now() + POLL_TIMEOUT_MS
  for (;;) {
    const record = await zeusChatApi.response(conversationId)
    if (record.status !== 'pending') return record
    if (Date.now() >= deadline) {
      return {
        ...record,
        status: 'error',
        error: 'Zeus is taking longer than expected. Check back shortly.',
      }
    }
    await delay(POLL_INTERVAL_MS)
  }
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

function MessageBubble({ msg }: { msg: ZeusChatMessage }) {
  const isZeus = msg.role === 'zeus'
  return (
    <div className={`flex ${isZeus ? 'justify-start' : 'justify-end'}`}>
      <div
        className={`max-w-[80%] rounded-lg px-4 py-3 ${
          isZeus
            ? 'bg-cmd-container border border-cmd-outline/20'
            : 'bg-primary/10 border border-primary/20'
        }`}
      >
        <div className="flex items-center gap-2 mb-1">
          {isZeus && (
            <span className="text-primary font-mono text-xs font-bold tracking-widest">⚡ ZEUS</span>
          )}
          {!isZeus && (
            <span className="text-cmd-outline font-mono text-xs font-bold tracking-widest">MARK</span>
          )}
          <span className="text-cmd-outline text-xs">{formatTime(msg.timestamp)}</span>
        </div>
        <p className="text-white/90 text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
      </div>
    </div>
  )
}

function ThinkingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="bg-cmd-container border border-cmd-outline/20 rounded-lg px-4 py-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-primary font-mono text-xs font-bold tracking-widest">⚡ ZEUS</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-cmd-outline text-sm">Thinking</span>
          <span className="flex gap-0.5">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="h-1 w-1 rounded-full bg-primary animate-pulse"
                style={{ animationDelay: `${i * 200}ms` }}
              />
            ))}
          </span>
        </div>
      </div>
    </div>
  )
}

export function ZeusChat() {
  const [messages, setMessages] = useState<ZeusChatMessage[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    let cancelled = false
    zeusChatApi
      .history()
      .then(({ messages: hist }) => {
        if (!cancelled) setMessages(hist)
      })
      .catch(() => {
        // No history yet or fetch failed — start fresh
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  async function send() {
    const text = input.trim()
    if (!text || loading) return

    const userMsg: ZeusChatMessage = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    }

    setMessages((prev) => [...prev, userMsg])
    setInput('')
    setLoading(true)
    setError(null)

    try {
      // Zeus runs asynchronously — POST returns a conversation ID immediately,
      // then we poll the response record until it completes or errors.
      const { conversationId } = await zeusChatApi.send(text)
      const record = await pollForResponse(conversationId)

      if (record.status === 'complete' && record.response) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'zeus',
            content: record.response as string,
            timestamp: record.completedAt ?? new Date().toISOString(),
          },
        ])
      } else {
        setError(record.error ?? 'Zeus did not respond. Try again.')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Try again.')
    } finally {
      setLoading(false)
      inputRef.current?.focus()
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void send()
    }
  }

  const empty = !historyLoading && messages.length === 0

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem-2rem)] sm:h-[calc(100vh-3.5rem-3rem)] -m-4 sm:-m-6 bg-cmd-base">
      {/* Header */}
      <div className="flex-none px-4 sm:px-6 py-4 border-b border-cmd-outline/10 bg-cmd-low">
        <div className="flex items-center gap-3">
          <span className="text-2xl">⚡</span>
          <div>
            <h2 className="font-mono text-sm font-bold tracking-widest text-white uppercase">
              Zeus · Council CEO
            </h2>
            <p className="text-cmd-outline text-xs">Direct channel — messages are logged and remembered</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6 space-y-4">
        {historyLoading && (
          <div className="flex justify-center pt-8">
            <span className="text-cmd-outline text-sm animate-pulse">Loading history…</span>
          </div>
        )}

        {empty && (
          <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
            <span className="text-5xl opacity-20">⚡</span>
            <p className="text-cmd-outline text-sm max-w-sm">
              Start a conversation with Zeus. Ask about business strategy, agent performance,
              decisions, or anything Council-related.
            </p>
          </div>
        )}

        {messages.map((msg, i) => (
          <MessageBubble key={`${msg.timestamp}-${i}`} msg={msg} />
        ))}

        {loading && <ThinkingIndicator />}

        {error && (
          <div className="flex justify-center">
            <p className="text-cmd-error text-xs bg-cmd-error/10 border border-cmd-error/20 rounded px-3 py-2">
              {error}
            </p>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="flex-none px-4 sm:px-6 py-4 border-t border-cmd-outline/10 bg-cmd-low">
        <div className="flex gap-3 items-end">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Message Zeus… (Enter to send, Shift+Enter for new line)"
            disabled={loading}
            rows={2}
            className="flex-1 resize-none rounded-lg bg-cmd-container border border-cmd-outline/20 px-4 py-3 text-sm text-white/90 placeholder:text-cmd-outline focus:outline-none focus:border-primary/50 transition-colors disabled:opacity-50"
          />
          <button
            onClick={() => void send()}
            disabled={loading || !input.trim()}
            className="flex-none h-[4.5rem] px-5 rounded-lg bg-primary text-cmd-base font-mono text-xs font-bold tracking-widest uppercase transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {loading ? '…' : 'SEND'}
          </button>
        </div>
        <p className="mt-2 text-cmd-outline text-[0.65rem]">
          Shift+Enter for new line · Chat is persistent across sessions
        </p>
      </div>
    </div>
  )
}
